/**
 * Service detail pages (/services/:slug).
 *
 * Copy is drafted from existing site content and needs practitioner sign-off.
 * Pages render with noindex and stay out of the sitemap until `reviewed` is
 * set to true.
 */

export const massagePricing = [
  { duration: '30 minutes', price: '$100' },
  { duration: '45 minutes', price: '$115' },
  { duration: '60 minutes', price: '$140' },
  { duration: '75 minutes', price: '$165' },
  { duration: '90 minutes', price: '$190' },
]

export const athleticPricing = [
  { duration: 'Initial Assessment (60 minutes)', price: '$140' },
  { duration: '30 minutes', price: '$90' },
  { duration: '45 minutes', price: '$115' },
  { duration: '60 minutes', price: '$140' },
]

export const naturopathicPricing = [
  { duration: 'Complimentary 15-minute consultation', price: 'Free' },
  { duration: '60-minute initial', price: '$250' },
  { duration: '75-minute follow-up', price: '$225' },
  { duration: '60-minute follow-up', price: '$200' },
  { duration: '45-minute follow-up', price: '$150' },
  { duration: '30-minute follow-up', price: '$110' },
  { duration: '15-minute phone follow-up', price: '$50' },
]

const RMTS = ['olivia-embry', 'brandon-schneider', 'jazmin-ulalan', 'miranda-manolakos']

export const servicePages = [
  {
    slug: 'massage-therapy',
    name: 'Massage Therapy',
    title: 'Registered Massage Therapy at Yonge & Eglinton',
    description:
      'Registered massage therapy in Midtown Toronto for neck and back pain, headaches, stress, and recovery. 30–90 minute sessions from $100.',
    eyebrow: 'Registered Massage Therapy',
    h1: 'Registered Massage Therapy in Midtown Toronto',
    intro:
      'Hands-on treatment to help reduce pain, relieve tension, improve mobility, and support recovery — from Registered Massage Therapists who tailor every session to your body and goals.',
    image: '/services/massage-therapy.jpg',
    imageAlt: 'Registered massage therapist treating a client’s shoulder at Recreo Rehab',
    sections: [
      {
        heading: 'What massage therapy at Recreo looks like',
        body: [
          'Our massage therapists are experts in hands-on care. Each session starts with a conversation about what you are feeling and what you want to get back to, followed by an assessment of how you move. From there, your RMT chooses the manual therapy techniques best suited to your goals.',
          'Depending on your needs, treatment may include deep tissue massage, joint mobilizations, cupping, acupuncture, and corrective exercises you can continue at home.',
        ],
      },
      {
        heading: 'More than a relaxation massage',
        body: [
          'Many of our RMTs also have backgrounds in kinesiology, osteopathic training, and sport massage. That means we look at the contributing factors behind your pain — posture, movement habits, training load, and stress — not just the spot that hurts.',
        ],
      },
    ],
    benefitsTitle: 'Common concerns we support',
    benefits: [
      'Neck & back pain',
      'Headaches and jaw tension',
      'Stress and burnout',
      'Pregnancy and postpartum care',
      'General maintenance and recovery',
    ],
    pricing: massagePricing,
    pricingNote:
      'Acupuncture, cupping, and TMJ care can be included in a massage therapy session at no extra cost.',
    practitioners: RMTS,
    faqs: [
      {
        q: 'How long should my first massage therapy appointment be?',
        a: 'Most people book 60 minutes for a first visit so there is time for a history, assessment, and treatment. If you are short on time or treating one specific area, 45 minutes works well.',
      },
      {
        q: 'Is massage therapy covered by insurance?',
        a: 'Many extended health plans cover registered massage therapy. Direct billing is available where applicable, and you always receive a receipt from your RMT to submit to your insurer.',
      },
      {
        q: 'What pressure will my RMT use?',
        a: 'Pressure is always adjusted to you. Each practitioner’s profile lists their usual range, from moderate to deep, and you can ask for changes at any point during treatment.',
      },
    ],
    relatedConditions: ['neck-back-pain', 'tmj-headaches', 'desk-worker-pain'],
    relatedServices: ['sports-massage', 'prenatal-massage', 'acupuncture'],
    bookLabel: 'Book Massage Therapy',
    reviewed: false,
  },
  {
    slug: 'athletic-therapy',
    name: 'Athletic Therapy',
    title: 'Athletic Therapy in Midtown Toronto',
    description:
      'Certified Athletic Therapist at Yonge & Eglinton for sports injuries, sprains, strains, pre- and post-surgical rehab, and return to activity.',
    eyebrow: 'Athletic Therapy',
    h1: 'Athletic Therapy at Yonge & Eglinton',
    intro:
      'Assessment, hands-on treatment, and therapeutic exercise from a Certified Athletic Therapist — so you can move better, recover confidently, and return to the activities you love.',
    image: '/services/athletic-therapy.jpg',
    imageAlt: 'Athletic therapist guiding a client through a functional movement exercise',
    sections: [
      {
        heading: 'What is athletic therapy?',
        body: [
          'Athletic Therapy specializes in the prevention, assessment, and treatment of musculoskeletal injuries and conditions. Using a combination of hands-on techniques and therapeutic exercise, Athletic Therapists help manage pain, restore movement and strength, and prevent future injuries.',
          'Certified Athletic Therapists follow the Sports Medicine Model of care, working with clients to move better, recover confidently, and return to the activities they love.',
        ],
      },
      {
        heading: 'Your first appointment',
        body: [
          'Your initial assessment is 60 minutes. Your therapist reviews your injury history and goals, assesses how you move, and explains what they find. You leave with hands-on treatment and a clear exercise plan built around your schedule.',
          'Follow-up visits focus on progressing that plan — adding strength, mobility, and sport- or task-specific work as you recover.',
        ],
      },
    ],
    benefitsTitle: 'Who can benefit from athletic therapy?',
    benefits: [
      { label: 'Athletes', detail: 'from recreational to high-performance' },
      {
        label: 'Active individuals',
        detail: 'anyone dealing with an injury or looking to improve strength, mobility & performance',
      },
      {
        label: 'Desk workers',
        detail: 'individuals experiencing neck, shoulder, back or postural-related discomfort',
      },
      {
        label: '“Athletes of life”',
        detail: 'individuals who want to stay active & capable in their everyday lives',
      },
      {
        label: 'Individuals recovering from injuries',
        detail:
          'including sprains, strains, joint-related injuries/conditions, muscle-related injuries/conditions & any other musculoskeletal (MSK) conditions',
      },
      {
        label: 'Pre- & post-surgical clients',
        detail: 'to strengthen prior to surgery or to assist with movement and function post-surgery',
      },
      {
        label: 'Individuals looking to prevent injuries',
        detail: 'through strength, mobility, movement assessment & education',
      },
    ],
    pricing: athleticPricing,
    practitioners: ['miranda-manolakos'],
    faqs: [
      {
        q: 'What is the difference between athletic therapy and physiotherapy?',
        a: 'Both treat musculoskeletal injuries. Athletic therapists are trained specifically in the Sports Medicine Model, with a strong focus on active rehabilitation, exercise, and return to activity for people of every fitness level — not only athletes.',
      },
      {
        q: 'Do I need a referral for athletic therapy?',
        a: 'No referral is needed to book. Some insurance plans ask for a physician’s note before they reimburse, so check your plan if you intend to claim.',
      },
      {
        q: 'Is athletic therapy covered by insurance?',
        a: 'Many extended health plans include athletic therapy by a Certified Athletic Therapist, but coverage varies. Check your plan before your visit.',
      },
    ],
    relatedConditions: ['running-injuries', 'shoulder-pain', 'neck-back-pain'],
    relatedServices: ['sports-massage', 'massage-therapy'],
    bookLabel: 'Book Athletic Therapy',
    reviewed: false,
  },
  {
    slug: 'naturopathic-medicine',
    name: 'Naturopathic Medicine',
    title: 'Naturopathic Doctor in Midtown Toronto',
    description:
      'Root-cause naturopathic care at Yonge & Eglinton for fatigue, stress, hormones, digestion, and skin. Free 15-minute consultation.',
    eyebrow: 'Naturopathic Medicine',
    h1: 'Naturopathic Medicine at Yonge & Eglinton',
    intro:
      'Evidence-based, root-cause care that looks at sleep, nutrition, stress, movement, hormones, and lifestyle — the foundations of how you feel and function every day.',
    image: '/services/consultation.jpg',
    imageAlt: 'Naturopathic doctor consulting with a patient in a sunlit treatment room',
    sections: [
      {
        heading: 'A whole-person approach',
        body: [
          'Naturopathic medicine considers the many factors that influence how you feel and function every day, including sleep, nutrition, stress, movement, hormones, and lifestyle habits. By addressing these foundations of health, treatment is designed to support recovery, build resilience, and help you feel your best over the long term.',
        ],
      },
      {
        heading: 'What treatment may include',
        body: [
          'Care may include nutrition and lifestyle guidance, acupuncture, botanical medicine, supplementation, and laboratory testing, depending on your needs and goals. Your plan is individualized and reviewed at each follow-up.',
        ],
      },
      {
        heading: 'Start with a free consultation',
        body: [
          'Not sure if naturopathic medicine is right for you? Book a complimentary 15-minute consultation to talk through your concerns and what care could look like.',
        ],
      },
    ],
    benefitsTitle: 'Common concerns we support',
    benefits: [
      'Fatigue, low energy, high stress, sleep issues',
      'Hormonal health (PMS, PCOS, fertility, thyroid, perimenopause, menstrual concerns)',
      'Stress & nervous system regulation',
      'Digestive health',
      'Blood sugar balance & metabolic health (weight loss, cholesterol, blood pressure)',
      'Skin health',
      'Pain management',
    ],
    pricing: naturopathicPricing,
    practitioners: ['alana-shnier'],
    faqs: [
      {
        q: 'What happens at a first naturopathic appointment?',
        a: 'Your 60-minute initial visit covers your health history, current symptoms, lifestyle, and goals in depth. Your naturopathic doctor may recommend lab testing and will outline a first set of recommendations.',
      },
      {
        q: 'Is naturopathic medicine covered by insurance?',
        a: 'Many extended health plans include naturopathic medicine. Coverage amounts vary, so check your plan before your visit.',
      },
    ],
    relatedConditions: ['pregnancy-postpartum'],
    relatedServices: ['acupuncture', 'massage-therapy'],
    bookLabel: 'Book Naturopathic Medicine',
    reviewed: false,
  },
  {
    slug: 'acupuncture',
    name: 'Acupuncture',
    title: 'Acupuncture at Yonge & Eglinton, Toronto',
    description:
      'Neurofunctional and contemporary medical acupuncture in Midtown Toronto to help manage pain, support recovery, and improve sleep.',
    eyebrow: 'Neurofunctional Acupuncture',
    h1: 'Acupuncture in Midtown Toronto',
    intro:
      'Targeted treatment used to help manage pain, support recovery, improve sleep, and regulate the nervous system — offered as part of massage therapy and naturopathic appointments.',
    image: '/services/acupuncture.jpg',
    imageAlt: 'Acupuncture needles placed along a client’s upper back',
    sections: [
      {
        heading: 'How we use acupuncture',
        body: [
          'Several of our Registered Massage Therapists are Contemporary Medical Acupuncture providers. They may use acupuncture alongside hands-on treatment to target specific muscles and nerves and help calm an irritated nervous system.',
          'Our naturopathic doctor also offers acupuncture as part of a broader treatment plan, including facial acupuncture.',
        ],
      },
      {
        heading: 'What to expect',
        body: [
          'Acupuncture uses fine, single-use sterile needles. Most people feel a brief pinch or a dull ache as the needle settles. Your practitioner will explain the plan first, and you can always choose not to include acupuncture in your session.',
        ],
      },
    ],
    benefitsTitle: 'Acupuncture may help with',
    benefits: [
      'Neck, back, and shoulder pain',
      'Headaches and jaw tension',
      'Muscle tension and recovery',
      'Stress and sleep',
      'Nervous system regulation',
    ],
    pricing: massagePricing,
    pricingNote:
      'Acupuncture is included within massage therapy and naturopathic appointments and priced by appointment length. Massage therapy rates are shown; see Naturopathic Medicine for those rates.',
    practitioners: ['olivia-embry', 'brandon-schneider', 'jazmin-ulalan', 'alana-shnier'],
    faqs: [
      {
        q: 'Does acupuncture hurt?',
        a: 'Most people feel very little. The needles are much finer than those used for injections. You may feel a brief pinch or a heavy, dull sensation, which usually settles quickly.',
      },
      {
        q: 'How do I book acupuncture?',
        a: 'Book a massage therapy appointment with one of our acupuncture-trained RMTs, or a naturopathic appointment, and let your practitioner know you would like acupuncture included.',
      },
    ],
    relatedConditions: ['neck-back-pain', 'tmj-headaches', 'sciatica'],
    relatedServices: ['massage-therapy', 'naturopathic-medicine'],
    bookLabel: 'Book Acupuncture',
    reviewed: false,
  },
  {
    slug: 'tmj-treatment',
    name: 'TMJ & Jaw Care',
    title: 'TMJ & Jaw Pain Treatment in Toronto',
    description:
      'TMJ treatment in Midtown Toronto for jaw pain, clenching, headaches, and facial tension, including intra-oral massage by trained RMTs.',
    eyebrow: 'TMJ & Jaw Care',
    h1: 'TMJ & Jaw Pain Treatment in Midtown Toronto',
    intro:
      'Support for jaw pain, clenching, headaches, facial tension, and related neck discomfort from Registered Massage Therapists with advanced TMJ and intra-oral training.',
    image: '/services/pain-management.jpg',
    imageAlt: 'Massage therapist treating the jaw and neck area',
    sections: [
      {
        heading: 'Why jaw tension spreads',
        body: [
          'The muscles that move your jaw connect closely with those of the head and neck. Clenching, grinding, stress, and long hours at a desk can all load these muscles, which is why jaw tension often shows up alongside headaches and neck pain.',
        ],
      },
      {
        heading: 'How we treat TMJ',
        body: [
          'Treatment combines external work on the jaw, face, neck, and shoulders with intra-oral massage — gloved work inside the mouth to reach the jaw muscles directly — when appropriate and with your consent. Your RMT may also show you self-massage and habit strategies to use between visits.',
        ],
      },
    ],
    benefitsTitle: 'TMJ care may help with',
    benefits: [
      'Jaw pain, clicking, or stiffness',
      'Clenching and grinding',
      'Tension headaches',
      'Facial tension',
      'Related neck discomfort',
    ],
    pricing: massagePricing,
    pricingNote: 'TMJ care is provided within a massage therapy session and billed as massage therapy.',
    practitioners: ['olivia-embry'],
    faqs: [
      {
        q: 'What is intra-oral massage?',
        a: 'Intra-oral massage is gloved treatment inside the mouth that reaches jaw muscles you cannot access from the outside. It is always explained first and only done with your consent.',
      },
      {
        q: 'Is TMJ treatment covered by insurance?',
        a: 'TMJ care is delivered as part of a registered massage therapy session, so it is billed as massage therapy under your extended health plan.',
      },
    ],
    relatedConditions: ['tmj-headaches', 'desk-worker-pain'],
    relatedServices: ['massage-therapy', 'acupuncture'],
    bookLabel: 'Book TMJ Care',
    reviewed: false,
  },
  {
    slug: 'prenatal-massage',
    name: 'Prenatal & Postpartum',
    title: 'Prenatal & Postpartum Massage in Toronto',
    description:
      'Prenatal and postpartum massage therapy at Yonge & Eglinton to ease back pain, tension, and discomfort through pregnancy and recovery.',
    eyebrow: 'Prenatal & Postpartum Care',
    h1: 'Prenatal & Postpartum Massage in Midtown Toronto',
    intro:
      'Treatment designed to support the changing demands of pregnancy and postpartum recovery, from RMTs with a pre- and post-natal focus.',
    image: '/services/prenatal.jpg',
    imageAlt: 'Pregnant client receiving side-lying massage therapy',
    sections: [
      {
        heading: 'Care through every stage',
        body: [
          'Pregnancy changes how your muscles and joints carry load, and postpartum life brings new demands — feeding, lifting, and less sleep. Massage therapy can help reduce tension and discomfort through the back, hips, neck, and shoulders at each stage.',
        ],
      },
      {
        heading: 'Comfortable positioning',
        body: [
          'Your RMT uses side-lying and supported positions so you can relax safely and comfortably. Let us know how far along you are when you book, and tell your therapist about any guidance from your healthcare provider.',
        ],
      },
      {
        heading: 'Whole-body support',
        body: [
          'Our naturopathic doctor can also support energy, recovery, hormones, and overall wellness during pregnancy and postpartum.',
        ],
      },
    ],
    benefitsTitle: 'Prenatal & postpartum massage may help with',
    benefits: [
      'Low back and hip pain',
      'Neck and shoulder tension',
      'Swelling and heaviness in the legs',
      'Sleep and stress',
      'Postpartum recovery and feeding posture',
    ],
    pricing: massagePricing,
    practitioners: ['jazmin-ulalan', 'olivia-embry', 'brandon-schneider', 'miranda-manolakos'],
    faqs: [
      {
        q: 'When can I start prenatal massage?',
        a: 'Many people start in the second trimester, though massage can be adapted throughout pregnancy. Follow any guidance from your healthcare provider and let your RMT know about it.',
      },
      {
        q: 'Will I be able to lie on my stomach?',
        a: 'Treatment is done side-lying or in a supported position, so you do not need to lie on your stomach.',
      },
    ],
    relatedConditions: ['pregnancy-postpartum', 'neck-back-pain'],
    relatedServices: ['massage-therapy', 'naturopathic-medicine'],
    bookLabel: 'Book Prenatal Massage',
    reviewed: false,
  },
  {
    slug: 'sports-massage',
    name: 'Sports Massage',
    title: 'Sports Massage in Midtown Toronto',
    description:
      'Sports massage and active recovery at Yonge & Eglinton for runners, lifters, and recreational athletes. Training, recovery, and performance.',
    eyebrow: 'Sports & Active Recovery',
    h1: 'Sports Massage & Active Recovery in Midtown Toronto',
    intro:
      'Support for training, performance, recovery, and maintaining an active lifestyle — from therapists who play, train, and understand what your sport asks of your body.',
    image: '/services/sports-massage.jpg',
    imageAlt: 'Therapist performing sports massage on a runner’s leg',
    sections: [
      {
        heading: 'Built around your training',
        body: [
          'Sports massage combines hands-on work with functional assessment, mobility, and exercise to help you recover between sessions, manage niggles before they become injuries, and prepare for race or game day.',
          'Our team includes an RMT with CSMTA Advanced Sport Massage training, a Certified Athletic Therapist, and therapists with Titleist Performance Institute and Functional Range Conditioning certifications.',
        ],
      },
      {
        heading: 'When to book',
        body: [
          'Regular sessions during heavy training blocks, a lighter session a few days before an event, and a recovery session afterwards work well for most athletes. Your therapist will help you plan around your calendar.',
        ],
      },
    ],
    benefitsTitle: 'Sports massage may help with',
    benefits: [
      'Recovery between training sessions',
      'Hip, leg, and knee tightness',
      'Mobility for lifting, running, and golf',
      'Pre- and post-event preparation',
      'Managing overuse aches before they become injuries',
    ],
    pricing: massagePricing,
    practitioners: ['jazmin-ulalan', 'brandon-schneider', 'miranda-manolakos'],
    faqs: [
      {
        q: 'Should I book sports massage or athletic therapy?',
        a: 'Book sports massage for recovery, maintenance, and general tightness. Book athletic therapy if you have a specific injury that needs assessment and a rehab plan.',
      },
      {
        q: 'Is sports massage covered by insurance?',
        a: 'Sports massage is provided by Registered Massage Therapists, so it is billed as massage therapy under your extended health plan.',
      },
    ],
    relatedConditions: ['running-injuries', 'shoulder-pain'],
    relatedServices: ['athletic-therapy', 'massage-therapy'],
    bookLabel: 'Book Sports Massage',
    reviewed: false,
  },
]

export const serviceBySlug = (slug) => servicePages.find((s) => s.slug === slug)
